import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import TopNavbar from "./TopNavbar";
import BannerTop from "../ads/BannerTop";
import { Footer } from "./Footer";
import { Breadcrumbs } from "../navigation/Breadcrumbs";
import MobileNavBar from "../navigation/MobileNavBar";
import MobileBannerSticky from "../ads/MobileBannerSticky";
import { useScrollToTopOnMount } from "../../hooks/useScrollToTop";
import HighPerformanceBanner from "../ads/HighPerformanceBanner";


const LayoutWrapper = styled.div<{ $tablet?: boolean }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-top: 60px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding-top: 0; // Supprimer le padding supérieur sur mobile
    padding-bottom: 80px; // Espace pour la barre de navigation mobile
  }
`;

const Main = styled.main<{ $tablet?: boolean }>`
  flex: 1;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 var(--spacing-4) var(--spacing-4);
  min-height: calc(100vh - var(--header-height) - var(--footer-height));

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 var(--spacing-3) var(--spacing-3);
    min-height: calc(100vh - var(--footer-height) - 80px); // Ajuster la hauteur minimale pour mobile
    padding-bottom: calc(var(--spacing-4) + 70px); // Padding pour la barre de navigation mobile
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const MobileTopAd = styled.div`
  display: none;
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 6px 8px 4px;
    /* Décale la pub sous la barre du haut pour éviter la coupure */
    margin-top: calc(72px + env(safe-area-inset-top, 0px));
    margin-bottom: 6px;
    background: transparent;
  }
`;

const MobileTopAdCard = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 100%;
  padding: 0; /* éviter le rognage visuel */
  border-radius: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
`;

interface LayoutProps {
  children: React.ReactNode;
  breadcrumbs?: Array<{
    label: string;
    path: string;
  }>;
  $backdrop?: boolean;
  $tablet?: boolean;
}

import { useEffect } from "react";

export const Layout: React.FC<LayoutProps> = ({ children, breadcrumbs, $backdrop, $tablet }) => {
  // Scroll automatique vers le haut à chaque changement de page
  useScrollToTopOnMount();

  useEffect(() => {
    // MutationObserver pour supprimer tout texte parasite "color: purple;" dès qu'il apparaît
    const removePurpleText = () => {
      const body = document.body;
      if (body && body.childNodes.length > 0) {
        body.childNodes.forEach(node => {
          if (
            node.nodeType === Node.TEXT_NODE &&
            node.textContent &&
            node.textContent.trim().startsWith("color: purple;")
          ) {
            node.textContent = "";
          }
        });
      }
    };
    // Appel immédiat au cas où le texte est déjà là
    removePurpleText();
    // Observe toute modification du body
    const observer = new MutationObserver(removePurpleText);
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);

  const [isMobile, setIsMobile] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Failsafe: réinitialiser tout overflow bloquant à chaque changement de route
  React.useEffect(() => {
    const html = document.documentElement as HTMLElement;
    document.body.style.overflow = '';
    html.style.overflow = '';
  }, [location.pathname]);

  return (
    <LayoutWrapper $tablet={$tablet}>
      <TopNavbar />
      {/* Bannière Adsterra en haut - masquée sur mobile */}
      {!isMobile && <BannerTop />}
      {/* Bannière mobile juste sous "Cinédelux" */}
      {isMobile && (
        <MobileTopAd>
          <MobileTopAdCard>
            <HighPerformanceBanner
              adKey="2154f7f7df439957dfc24240cd37dd3c"
              width={728}
              height={90}
              mobileWidth={320}
              mobileHeight={50}
              id="hp-banner-layout-mobile-top"
            />
          </MobileTopAdCard>
        </MobileTopAd>
      )}
      <Main $tablet={$tablet}>
        <ContentWrapper>
          {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
          {children}
        </ContentWrapper>
      </Main>
      <Footer />

      <MobileNavBar />

    </LayoutWrapper>
  );
};
